import { Button, Flex, Group, IconFont, Text } from '@byecode/ui'
import type { SpaceQuota, SpaceVersion } from '@lighthouse/core'
import { DEFAULT_SPACE_VERSION, defaultZhDateFormat, spaceGradeNameMap, spaceVersionEnum } from '@lighthouse/shared'
import { getFileSizeAccordanceBaseUnit } from '@lighthouse/tools'
import { lightFormat } from 'date-fns'
import { groupBy } from 'rambda'
import React, { useMemo, useState } from 'react'
import styled from 'styled-components'

import { openSpaceGrade } from '@/components/SpaceGrade'

import { SpaceQuotaSlider } from '../Slider'
import { SliderLabel } from '../Slider/SliderLabel'

interface SpaceQuotaFlowProps {
    version: SpaceVersion
    data: SpaceQuota['traffic']
}

const SCxContainer = styled.div`
    width: 100%;
    padding: 20px;
    border-radius: 12px;
    border: 1px solid var(--color-gray-200);
    background: var(--color-white);
`

const SCxTitle = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 20px;
    gap: 8px;
`

const SCxContent = styled.div`
    margin-top: 16px;
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 16px;
    border-radius: 8px;
    background-color: var(--color-gray-50);
`

const SCxDescribe = styled.div`
    margin-top: 8px;
    display: flex;
    flex-direction: column;
    gap: 8px;
    font-size: 12px;
    line-height: 20px;
    color: var(--color-gray-400);
`

export const SpaceQuotaFlow: React.FunctionComponent<SpaceQuotaFlowProps> = ({ data, version }) => {
    const [opened, setOpened] = useState(true)

    const { quota, usage, packs } = data

    const purchaseTraffic = useMemo(() => groupBy(v => v.packType, packs), [packs])
    const displayPacks = useMemo(() => {
        const includePacks = purchaseTraffic?.INCLUDE ?? []
        const purchasedPacks = purchaseTraffic?.PURCHASED ?? []
        const allPacks = [...includePacks, ...purchasedPacks]

        let remainingUsage = usage

        const usageMap = new Map<string, number>()
        allPacks.forEach((pack, index) => {
            const key = `${pack.startTime ?? 'none'}-${pack.expiredAt ?? 'none'}-${pack.quota}-${index}`
            const isLast = index === allPacks.length - 1
            const allocatedUsage = isLast ? Math.max(remainingUsage, 0) : Math.min(Math.max(remainingUsage, 0), pack.quota)
            usageMap.set(key, allocatedUsage)
            remainingUsage -= allocatedUsage
        })

        const withDisplayUsage = <T extends typeof allPacks[number]>(items: T[], offset: number) =>
            items.map((item, index) => {
                const mapKey = `${item.startTime ?? 'none'}-${item.expiredAt ?? 'none'}-${item.quota}-${index + offset}`
                return {
                    ...item,
                    displayUsage: usageMap.get(mapKey) ?? 0
                }
            })

        return {
            include: withDisplayUsage(includePacks, 0),
            purchased: withDisplayUsage(purchasedPacks, includePacks.length)
        }
    }, [purchaseTraffic, usage])

    return (
        <SCxContainer>
            <SCxTitle>
                <Text weight={600} color="var(--color-black)" size={16}>
                    流量
                </Text>
                <Button type="default" onClick={() => openSpaceGrade('purchase')}>
                    购买流量包
                </Button>
            </SCxTitle>
            <SCxDescribe>
                访问应用页面、文件、图片、音频、视频等过程中产生的流量。当增购流量和版本流量都有余量时，会优先使用版本流量。当有效流量超额，将无法正常访问应用。
            </SCxDescribe>
            <Group
                styles={{
                    label: {
                        width: '100%'
                    },
                    wrapper: {
                        padding: '0!important'
                    },
                    collapse: {
                        padding: '0!important'
                    }
                }}
                opened={opened}
                label={
                    <SpaceQuotaSlider
                        style={{ fontWeight: 400 }}
                        data={{
                            icon: 'Earth',
                            label: (
                                <SliderLabel
                                    label="流量总额"
                                    usageLabel={getFileSizeAccordanceBaseUnit(usage, 'MB').replace('.0', '')}
                                    primaryColor={usage < quota ? 'var(--color-gray-600)' : 'var(--color-red-500)'}
                                    quotaLabel={getFileSizeAccordanceBaseUnit(quota, 'MB').replace('.0', '')}
                                    extra={
                                        <Flex alignItems="center" style={{ marginLeft: 8 }} onClick={() => setOpened(v => !v)}>
                                            <Text color="var(--color-main)" weight={400} size={12}>
                                                流量详情
                                            </Text>
                                            <IconFont
                                                type="ArrowDownSmall"
                                                size={16}
                                                color="var(--color-main)"
                                                style={{ transform: opened ? 'rotate(-180deg)' : undefined, transition: 'transform 0.2s' }}
                                            />
                                        </Flex>
                                    }
                                />
                            ),
                            usage,
                            quota,
                            primaryColor: 'var(--color-gray-500)'
                        }}
                    />
                }
                renderRightIcon={() => null}
            >
                <SCxContent>
                    {displayPacks.include.map(({ quota, displayUsage, expiredAt, startTime }, i) => {
                        const isExpired = expiredAt && expiredAt < Date.now()
                        const validity = expiredAt
                            ? isExpired
                                ? '已过期'
                                : `有效期至：${lightFormat(expiredAt, 'yyyy-MM-dd')} (剩余 ${Math.round(
                                      (expiredAt - Date.now()) / 1000 / 3600 / 24
                                  )}天)`
                            : '永久'
                        const key = startTime + i
                        return (
                            <SpaceQuotaSlider
                                key={key}
                                style={{ padding: 0, opacity: isExpired ? 0.5 : 1 }}
                                data={{
                                    label: (
                                        <SliderLabel
                                            label={
                                                <Flex gap={8}>
                                                    <Text color="var(--color-gray-600)">版本流量-{spaceGradeNameMap[version]}</Text>
                                                    <Text size={12} color="var(--color-gray-400)">
                                                        {validity}
                                                    </Text>
                                                </Flex>
                                            }
                                            usageLabel={getFileSizeAccordanceBaseUnit(displayUsage, 'MB').replace('.0', '')}
                                            primaryColor={displayUsage < quota ? 'var(--color-gray-600)' : 'var(--color-red-500)'}
                                            quotaLabel={getFileSizeAccordanceBaseUnit(quota, 'MB').replace('.0', '')}
                                        />
                                    ),
                                    usage: displayUsage,
                                    quota,
                                    primaryColor: 'var(--color-gray-500)'
                                }}
                            />
                        )
                    })}
                    {displayPacks.purchased.map(({ quota, displayUsage, expiredAt, startTime }, i) => {
                        const isExpired = expiredAt && expiredAt < Date.now()
                        const validity = expiredAt
                            ? isExpired
                                ? '已过期'
                                : `有效期至：${lightFormat(expiredAt, 'yyyy-MM-dd')} (剩余 ${Math.round(
                                      (expiredAt - Date.now()) / 1000 / 3600 / 24
                                  )}天)`
                            : '永久'
                        const key = `${startTime ?? 'none'}-${expiredAt ?? 'none'}-${quota}-${i}`
                        return (
                            <SpaceQuotaSlider
                                key={key}
                                style={{ padding: 0, opacity: isExpired ? 0.5 : 1 }}
                                data={{
                                    label: (
                                        <SliderLabel
                                            label={
                                                <Flex gap={8}>
                                                    <Text color="var(--color-gray-600)">
                                                        增购{getFileSizeAccordanceBaseUnit(quota, 'MB').replace('.0', '')}流量
                                                    </Text>
                                                    <Text size={12} color="var(--color-gray-400)">
                                                        {validity}
                                                    </Text>
                                                </Flex>
                                            }
                                            usageLabel={getFileSizeAccordanceBaseUnit(displayUsage, 'MB').replace('.0', '')}
                                            primaryColor={displayUsage < quota ? 'var(--color-gray-600)' : 'var(--color-red-500)'}
                                            quotaLabel={getFileSizeAccordanceBaseUnit(quota, 'MB').replace('.0', '')}
                                        />
                                    ),
                                    usage: displayUsage,
                                    quota,
                                    primaryColor: 'var(--color-gray-500)'
                                }}
                            />
                        )
                    })}
                </SCxContent>
            </Group>
        </SCxContainer>
    )
}
